import { Component, Input, OnDestroy, OnInit, inject, DestroyRef, AfterViewInit } from '@angular/core';
import _ from 'lodash';
import { select, Store } from '@ngrx/store';
import { filter, first } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { PendingService } from 'app/reports/services/pending.service';
import { SharedService } from 'app/virtual-event/services/shared.service';
import { selectDialogState } from 'app/dialog/features/dialog.selectors';
import { DialogService } from 'app/dialog/services/dialog.service';
import { MDBDropdownService } from 'app/core/services/mdb-dropdown.service';
import { closeDialog } from 'app/dialog/features/dialog.actions';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DonorService } from 'app/core/services/donor.service';
import { selectCurrentAuction } from 'app/manage-auctions/features/auction.selectors';
import { loadPendingDonations } from 'app/manage-auctions/features/auction.actions';
import { EmailNameSpliterService } from 'app/core/services/email-name-spliter.service';


@Component({
  selector: 'app-update-cart-modal',
  templateUrl: './update-cart-modal.component.html',
  styleUrls: ['./update-cart-modal.component.scss']
})
export class UpdateCartModalComponent implements OnInit, AfterViewInit, OnDestroy {
  destroyRef = inject(DestroyRef)
  public dialog$: Observable<any> = this.store.pipe(select(selectDialogState));
  public dialog: any = null;

  public form: UntypedFormGroup = new UntypedFormGroup({
    email: new UntypedFormControl("", [Validators.required, Validators.pattern(/^([a-zA-Z0-9_\.\-\+])+\@(([a-zA-Z0-9\-])+\.)+([a-zA-Z0-9]{2,4})+$/)]),
    name: new UntypedFormControl("",[Validators.required]),
  });

  private donorData = null;
  public isSameDonor = false;

  private allProfiles: any = null;
  public searchResult = [];

  @Input() id = '';
  @Input() cartid =''
  @Input() donorid = '';
  @Input() campaignid = ''
  @Input() email = ''
  @Input() eventid = '';
  @Input() veventid = '';

  public auctionCampaign: any;
  public auction$: any = this.store.pipe(
    select(selectCurrentAuction),
    takeUntilDestroyed()
  )
  
  constructor(
    public store: Store,
    public pendingService: PendingService,
    public sharedService: SharedService,
    public dialogService: DialogService,
    public dropdownService: MDBDropdownService,
    private donorService: DonorService,
    private emailNameSpliter: EmailNameSpliterService,
  ) { }

  ngOnInit(): void {
    this.dialog$.pipe(
      first(),
     takeUntilDestroyed(this.destroyRef)
    ).subscribe((dialog) => {
      this.dialog = dialog;
    })

    this.auction$.subscribe((auction)=>{
      this.auctionCampaign = auction;
    })
  }

  ngAfterViewInit() {
    this.setSearchSubscription('email', 0);
    this.setSearchSubscription('name', 1);
  }

  private setSearchSubscription(controlName: string, type: number){
    this.form.get(controlName).valueChanges
    .pipe(
      filter(value => _.isString(value)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((value) => {
      this.donorData = null;
      this.isSameDonor = false;

      if (value.length > 2) {
        this.searchDonor(value, type);
      } else {
        this.searchResult = [];
      }
    });
  }

  searchDonor(value: string, searchType: number = 0 | 1) {
    this.donorService.search(value, searchType)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((userProfiles) => {
        this.allProfiles = userProfiles;
        this.search(value);
    });
  }

  search(searchValue: string) {
    if (!this.allProfiles?.usersProfile) {
      this.searchResult = [];
      return;
    }

    const profileInfo = this.allProfiles.usersProfile.map(data => ({
      "data": data,
      "searchString": data.email + "/" + data.name
    }));

    const filteredResult = profileInfo.filter((item) => 
      item.searchString.toLowerCase().includes(searchValue.toLowerCase())
    );

    this.searchResult = filteredResult;
  }

  passOption(searchQuery: string | KeyboardEvent) {
    let searchString;
    if(searchQuery instanceof KeyboardEvent){
      searchString = (searchQuery.target as HTMLInputElement).value;
    } else {
      searchString = searchQuery as string;
    }

    const [email, name] = this.emailNameSpliter.getNameAndEmail(searchString);

    const selectedUser = this.allProfiles.usersProfile.find(user => 
      user.name === name && user.email === email
    );

    if (!selectedUser) {
      return;
    }

    this.donorData = selectedUser;

    this.form.patchValue({
      name: name,
      email: email
    }, { emitEvent: false });
  }

  close() {
    window['moveCartModal'].cancel();
  }

  onConfirm() {
    if(this.form.invalid) {
      this.form.markAllAsTouched();
      return false;
    }

    const donorNewEmail = this.form.get('email').value;
    const isSameDonor = this.checkIsSameDonor(donorNewEmail)
    const donorFrom = _.isObject(this.donorData) ? this.donorData : null;

    if(isSameDonor !== true){
      const data = this.getCallbackData()
      const isCampaignAuction = this.auctionCampaign?.id == this.campaignid;
      // reloadCallback is used to update angular dataTable when called from auction
      const reloadCallback = isCampaignAuction ? () => {
        this.store.dispatch(loadPendingDonations({ campaignId: this.campaignid }));
      } : null;
      
      window['orgMain'].updateDonorCart(data, donorFrom, reloadCallback);
      this.isSameDonor = false
      return;
    } else {
      this.isSameDonor = true
    } 
  }

  getCallbackData() {
    const { form } = this;

    const data: any = {
      email: form.get('email').value,
      name: form.get('name').value,
      emailFrom: this.email,
      donorId: this.donorid,
      cartId: this.cartid,
      campaignId: this.campaignid,
      prevEmail: form.get('email').value,
    }

    if(this.veventid){
      data.vEventId = this.veventid;
    }
    if(this.eventid){
      data.eventId = this.eventid;
    }

    return data;
  }

  checkIsSameDonor(email) {
    if (email == this.email) {
      return true
    }
    return false
  }
  
  ngOnDestroy(): void {
    const { initialState: { onDestroy } } = this.dialog;
    if(onDestroy){
      onDestroy();
    }
    try {
      this.dialogService.modalRef.close();
      this.dialogService.closeDialogSubject.next(null);
    } catch(e) {}

    this.store.dispatch({
      type: closeDialog.type,
      payload: null
    })
  }
}
